import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, finalize, map, take, takeUntil } from 'rxjs/operators';
import { IntegrationProvider } from '../../provider/integration.provider';
import { SharePointFilePickerService } from '../../services/sharepoint-file-picker.service';
import {
    DocumentSourceProvider,
    RequestedPermission,
    SelectedClosingFolder,
    SharepointConnection,
} from '../sharepoint-folders/sharepoint-folders.types';

/** How often the container re-checks whether the SharePoint connection has landed. */
const CONNECT_WATCH_INTERVAL_MS = 2500;

/** Give up watching for a connection after this long (the popup flow has its own timeout). */
const CONNECT_WATCH_TIMEOUT_MS = 3 * 60 * 1000;

/**
 * REV-21679 — Smart container for the SharePoint "Ordner hinzufügen" pure view.
 *
 * Wires the dumb `aumico-integration-sharepoint-folders` view to NgRx (via
 * `IntegrationProvider`) and to the Microsoft File Picker (via the injectable
 * `SharePointFilePickerService` seam). The container owns ALL side effects; the
 * pure view stays purely presentational.
 *
 * Placement: SharePoint is a document source, not an accounting vendor, so its
 * state is a dedicated sub-slice of IntegrationState (never an IntegrationVendor).
 */
@Component({
    selector: 'aumico-integration-sharepoint-folders-container',
    templateUrl: 'sharepoint-folders-container.component.html',
    styleUrls: ['sharepoint-folders-container.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SharepointFoldersContainerComponent implements OnInit, OnDestroy {
    /**
     * Popup name + size for the File Picker window this container opens synchronously
     * (REV-21057 BUG 2). Mirrors the docs' recommended picker size.
     */
    private static readonly PICKER_WINDOW_NAME = 'sharepoint-file-picker';
    private static readonly PICKER_WINDOW_FEATURES = 'width=1080,height=680';

    /** The closing project whose SharePoint folders this container manages. */
    @Input() public projectId!: string;

    /**
     * Whether the "Manueller Upload" tile can be picked.
     *
     * ⚠️ OPT-IN, and default `false` on purpose. The tile was switched on globally for the
     * Setup-Wizard, which also turned it on in the closing's Dokumente-Tab — and that surface mounts
     * this container WITHOUT `(providerSelected)`, so clicking it there did nothing at all
     * (Code-Review Runde 1, M1). A host that cannot act on the choice must not be offered it.
     */
    @Input() public allowManualUpload = false;

    /**
     * Which document source the user picked (REV-21354).
     *
     * Emitted for the Setup-Wizard, which has to record the choice on the closing — the tile state
     * itself stays this container's business. Nothing outside the wizard listens, so the existing
     * integrations screen is unaffected.
     */
    @Output() public providerSelected = new EventEmitter<DocumentSourceProvider['id']>();

    /**
     * The tiles as this host should see them.
     *
     * Only `manual-upload` differs, and only upward: a host that binds `(providerSelected)` opts in
     * with `allowManualUpload` and gets a clickable tile; every other host keeps the disabled one it
     * had before REV-21354 (M1).
     */
    public get visibleProviders(): DocumentSourceProvider[] {
        return this.providers.map((provider) =>
            provider.id === 'manual-upload' ? { ...provider, enabled: this.allowManualUpload } : provider,
        );
    }

    public readonly connection$: Observable<SharepointConnection & { connectionId?: string }>;
    public readonly folders$: Observable<SelectedClosingFolder[]>;
    public readonly loading$: Observable<boolean>;

    /**
     * What the pure view actually renders: the store's connection, overlaid with the
     * local `awaiting-consent` step (REV-21057). Connected / connecting / error / the
     * connectionId all still come from the store — only the pre-request consent step is
     * local, because it exists before any request is made and has no server counterpart.
     */
    public readonly viewConnection$: Observable<SharepointConnection & { connectionId?: string }>;

    /** Which document-source tile is active. The SharePoint panel is the only live one. */
    public selectedProviderId: DocumentSourceProvider['id'] = 'sharepoint';

    /** Fallback so the pure view's non-nullable `connection` input never receives null. */
    public readonly defaultConnection: SharepointConnection = { status: 'disconnected' };

    /**
     * The document-source tiles. Built locally (this is view configuration, not
     * server state): SharePoint enabled, Google Drive + manual upload disabled
     * (coming later). Keys already exist in all locale files.
     */
    public readonly providers: DocumentSourceProvider[] = [
        {
            id: 'sharepoint',
            labelKey: 'aumico_integration_sharepoint_provider_sharepoint_label',
            descriptionKey: 'aumico_integration_sharepoint_provider_sharepoint_desc',
            icon: 'cloud',
            enabled: true,
        },
        {
            id: 'google-drive',
            labelKey: 'aumico_integration_sharepoint_provider_google_drive_label',
            descriptionKey: 'aumico_integration_sharepoint_provider_google_drive_desc',
            // `folder`, not `hard-drive`: only the icons registered in the app's
            // LUCIDE_ICON_SET (aumico-components) render — `hard-drive` is not one of them.
            icon: 'folder',
            enabled: false,
        },
        {
            id: 'manual-upload',
            // REV-21354 — picking it is a DECLARATION, not an upload: it records that this closing
            // has no watched folder and its documents arrive by hand through the hub's existing
            // upload modal. Nothing is uploaded from inside the wizard, which is why the tile needs
            // no picker of its own. Enabled only where a host listens — see `allowManualUpload`.
            labelKey: 'aumico_integration_sharepoint_provider_manual_upload_label',
            descriptionKey: 'aumico_integration_sharepoint_provider_manual_upload_desc',
            icon: 'upload',
            // `false` in the list, raised per host by `visibleProviders` below.
            enabled: false,
        },
    ];

    /**
     * The scopes listed on the in-app consent card. View configuration, not server state
     * (it mirrors the scopes the BE asks Microsoft for). Two lines on purpose: aumico
     * requests more than one scope, and the card is the user's only chance to see them
     * when the tenant has already granted admin consent.
     */
    public readonly requestedPermissions: RequestedPermission[] = [
        {
            labelKey: 'aumico_integration_sharepoint_consent_permission_files_read',
            descriptionKey: 'aumico_integration_sharepoint_consent_permission_files_read_desc',
        },
        {
            labelKey: 'aumico_integration_sharepoint_consent_permission_sites_read',
        },
    ];

    /** True between the connect click and "Zugriff erlauben" / "Abbrechen". */
    private readonly consentPending$ = new BehaviorSubject<boolean>(false);

    /**
     * True from 'Zugriff erlauben' until the connect settles (connected, error, or the
     * watch deadline).
     *
     * The reducer DOES set `connecting` when the flow starts — but `watchForConnection`
     * then polls the BE every 2.5s, and `sharepointLoadConnectionSuccess` replaces the whole
     * connection object with the server's, which is still `disconnected` until the callback
     * has run. So the first poll tick clobbered `connecting` and the user watched the
     * disconnected explainer (with its 'connect' button) for the seconds it took Microsoft to
     * come back. Owning the in-flight fact in the container makes the view independent of poll
     * timing — the same reason `consentPending$` is a local overlay rather than store state.
     */
    private readonly connectInFlight$ = new BehaviorSubject<boolean>(false);

    /** Message under "Ordner hinzufügen" when the picker cannot open; null hides it. */
    public readonly addFolderErrorKey$ = new BehaviorSubject<string | null>(null);

    /** Local snapshot of the connection so `onAddFolder` can read the connectionId synchronously. */
    private currentConnection: (SharepointConnection & { connectionId?: string }) | null = null;
    private readonly destroy$ = new Subject<void>();

    /**
     * The connectionId we have already prefetched a picker token/baseUrl for (REV-21057 BUG 2).
     * Guards against re-dispatching on every connection emission, while still re-fetching
     * when the connection itself changes.
     */
    private prefetchedPickerConnectionId: string | null = null;

    /**
     * The blank popup opened synchronously in `onAddFolder`, held so we can close it if the
     * picker never gets to open — and so a double-click cannot open a second one.
     */
    private pickerWindow: Window | null = null;

    /** Bounded poll while a connect is in flight — see `watchForConnection`. */
    private connectWatchTimer: ReturnType<typeof setTimeout> | undefined;

    /** Focus handler registered for the duration of a connect attempt. */
    private onWindowFocus: (() => void) | undefined;
    /** True once the window has been handed to the picker seam, which then owns closing it. */
    private pickerHandedOver = false;

    public constructor(
        private readonly integrationProvider: IntegrationProvider,
        private readonly pickerService: SharePointFilePickerService,
    ) {
        this.connection$ = this.integrationProvider.getSharepointConnection$();
        this.folders$ = this.integrationProvider.getSharepointFolders$();
        this.loading$ = this.integrationProvider.getSharepointLoading$();

        // The consent card only replaces the not-yet-connected states. As soon as the store
        // reports connecting/connected the real flow owns the UI again, so the local flag
        // cannot strand the user on the card.
        this.viewConnection$ = combineLatest([
            this.connection$,
            this.consentPending$,
            this.connectInFlight$,
        ]).pipe(
            map(([connection, consentPending, connectInFlight]) => {
                const preConnect =
                    connection.status === 'disconnected' || connection.status === 'error';
                if (consentPending && preConnect) {
                    return { ...connection, status: 'awaiting-consent' as const };
                }
                // Only over `disconnected`: if the BE reports an error the user must SEE the
                // error, not a spinner that never resolves.
                if (connectInFlight && connection.status === 'disconnected') {
                    return { ...connection, status: 'connecting' as const };
                }
                return connection;
            }),
        );
    }

    public ngOnInit(): void {
        this.integrationProvider.loadSharepointConnection(this.projectId);
        this.integrationProvider.loadSharepointFolders(this.projectId);

        this.connection$.pipe(takeUntil(this.destroy$)).subscribe((connection) => {
            this.currentConnection = connection;
            this.prefetchPickerData(connection);
        });

        // REV-22096 — a connection that CANNOT mint a token is not connected, whatever our row says.
        //
        // `GET …/sharepoint-connection` reads aumico's own row, and that row keeps saying CONNECTED
        // until something actually asks Microsoft for a token and is refused — which is exactly what
        // the prefetch above does the moment the view reports `connected`. When Microsoft refuses
        // (refresh token revoked, expired after 90 days without use, consent withdrawn), the BE marks
        // the row ERROR on the way out, but the banner in this very view still read "Verbunden als …"
        // and offered no way to sign in again. Re-reading the row after such a refusal is what turns
        // that banner into the connect button.
        //
        // `distinctUntilChanged` because the slice keeps the last error until the next request
        // clears it: one refusal, one re-read — not a re-read per emission.
        this.integrationProvider
            .getSharepointPickerTokenError$()
            .pipe(
                distinctUntilChanged(),
                filter((error): error is string => error != null),
                takeUntil(this.destroy$),
            )
            .subscribe(() => {
                if (this.currentConnection?.status === 'connected') {
                    this.integrationProvider.loadSharepointConnection(this.projectId);
                }
            });
    }

    /**
     * REV-21057 BUG 2 — warm the picker slice BEFORE the user clicks.
     *
     * "Ordner hinzufügen" must open the Microsoft picker on the FIRST click. The
     * click handler can only `window.open` synchronously (user-gesture rule), so everything
     * it needs — the picker token and, above all, the tenant `baseUrl` — should already be in
     * the store by then. We therefore fetch them as soon as the connection reports
     * `connected` with a `connectionId`, not on the click.
     *
     * Dispatched at most once per connectionId; a NEW connectionId re-fetches (a re-connect
     * can land on a different tenant/site).
     */
    private prefetchPickerData(
        connection: SharepointConnection & { connectionId?: string },
    ): void {
        const connectionId = connection.connectionId;
        if (connection.status !== 'connected' || connectionId == null) {
            return;
        }
        if (this.prefetchedPickerConnectionId === connectionId) {
            return;
        }
        this.prefetchedPickerConnectionId = connectionId;
        // A different connection may well have a SharePoint site; the old message no longer applies.
        this.addFolderErrorKey$.next(null);
        this.integrationProvider.getSharepointPickerToken(connectionId);
    }

    public ngOnDestroy(): void {
        this.stopWatchingForConnection();
        this.destroy$.next();
        this.destroy$.complete();
        this.consentPending$.complete();
        this.connectInFlight$.complete();
        this.addFolderErrorKey$.complete();
    }

    public onSelectProvider(providerId: DocumentSourceProvider['id']): void {
        this.selectedProviderId = providerId;
        this.providerSelected.emit(providerId);
    }

    /**
     * "Mit Microsoft 365 verbinden" — REV-21057: this no longer starts the flow. The
     * tenant may have granted admin consent, in which case Microsoft skips its own
     * consent screen, so aumico shows its own consent card first. The flow starts on
     * `onAllowConsent`.
     */
    public onConnect(): void {
        this.consentPending$.next(true);
    }

    /**
     * "Zugriff erlauben" on the in-app consent card — starts the EXISTING connect flow.
     * This runs inside a real user click, so the Microsoft popup the effect opens is
     * still inside a user gesture and is not blocked.
     */
    public onAllowConsent(): void {
        this.consentPending$.next(false);
        this.integrationProvider.startSharepointConnect(this.projectId);
        this.watchForConnection();
    }

    /**
     * Refresh the connection until it shows up, independently of the popup.
     *
     * The effect's popup watcher also polls, but it tears itself down the moment the
     * callback page signals success / the popup closes — and that teardown raced ahead
     * of the final reload, leaving the UI on the disconnected state until a manual page
     * refresh. Cross-origin popup messaging cannot be relied on here either: the app is
     * served from :4230 and the callback from :3800, so BroadcastChannel is out and
     * postMessage delivery is best-effort.
     *
     * So the container — which owns the view — does its own bounded polling, plus a
     * refresh when the window regains focus (i.e. the user came back from the popup).
     * Both stop as soon as the connection is `connected`.
     */
    private watchForConnection(): void {
        // Reset first — `stopWatchingForConnection` clears the in-flight flag, so raising it
        // must come after, or the restart would immediately cancel itself.
        this.stopWatchingForConnection();
        this.connectInFlight$.next(true);

        const deadline = Date.now() + CONNECT_WATCH_TIMEOUT_MS;
        const tick = (): void => {
            if (this.currentConnection?.status === 'connected') {
                this.stopWatchingForConnection();
                this.integrationProvider.loadSharepointFolders(this.projectId);
                return;
            }
            if (this.currentConnection?.status === 'error') {
                // A failed connect must surface as the error state, not keep spinning.
                this.stopWatchingForConnection();
                return;
            }
            if (Date.now() > deadline) {
                this.stopWatchingForConnection();
                return;
            }
            this.integrationProvider.loadSharepointConnection(this.projectId);
            this.connectWatchTimer = setTimeout(tick, CONNECT_WATCH_INTERVAL_MS);
        };
        this.connectWatchTimer = setTimeout(tick, CONNECT_WATCH_INTERVAL_MS);

        this.onWindowFocus = (): void => {
            if (this.currentConnection?.status === 'connected') return;
            this.integrationProvider.loadSharepointConnection(this.projectId);
        };
        window.addEventListener('focus', this.onWindowFocus);
    }

    /** Clears the connect watcher — safe to call repeatedly. */
    private stopWatchingForConnection(): void {
        // Every terminal path of the watch runs through here, so this is the one place the
        // in-flight flag has to be cleared.
        this.connectInFlight$.next(false);
        if (this.connectWatchTimer != null) {
            clearTimeout(this.connectWatchTimer);
            this.connectWatchTimer = undefined;
        }
        if (this.onWindowFocus != null) {
            window.removeEventListener('focus', this.onWindowFocus);
            this.onWindowFocus = undefined;
        }
    }

    /** "Abbrechen" on the in-app consent card — back to the disconnected explainer. */
    public onCancelConsent(): void {
        this.consentPending$.next(false);
    }

    /**
     * "Ordner hinzufügen" — open the Microsoft File Picker behind the injectable
     * seam, then persist whatever the user picked. No-op when there is no connected
     * connection yet.
     *
     * The picker mints its OWN per-resource tokens (the File Picker v8 asks the host for a
     * token per resource and rejects Graph tokens), so the container hands it the
     * `connectionId` rather than a token. The picker-token call is still dispatched here
     * because its response is what tells us the tenant SPO origin (`baseUrl`).
     *
     * REV-21057 BUG 2 — the popup MUST be opened synchronously here. `window.open` is only
     * permitted inside the user-gesture call stack; awaiting the picker token first pushed
     * the open outside it and Chrome blocked the popup, so the picker appeared to need a
     * second click (by then the token was already in the store and the observable emitted
     * synchronously). We therefore open a blank window now and hand the handle to the seam,
     * which navigates it once the token arrives.
     *
     * The SECOND half of that double click was our own teardown: while the token/baseUrl were
     * still in flight we closed the blank popup again, so the first click showed an empty
     * window that vanished. The window is now closed ONLY on a terminal outcome:
     *   - the picker-token fetch FAILED (`pickerTokenError`),
     *   - the response carried NO `baseUrl` (closed by the seam — it refuses to guess a host),
     *   - the picker resolved without a selection or threw (closed by the seam / the catch),
     *   - the component was destroyed while still waiting.
     * A response that has merely not arrived YET keeps the window open; we navigate it when
     * the data lands. Together with `prefetchPickerData` the data is normally already there,
     * so the first click goes straight to the Microsoft picker.
     *
     * Token freshness is a non-issue: the seam mints its OWN per-resource tokens for the
     * form-POST and for every `authenticate` command, so the slice value is only a readiness
     * signal. We still re-dispatch here to refresh the cached `baseUrl`.
     */
    public onAddFolder(): void {
        const connectionId = this.currentConnection?.connectionId;
        if (connectionId == null) {
            return;
        }
        // Double-click guard: a second click while a picker is pending must not open a
        // second window (and leave the first orphaned).
        if (this.pickerWindow != null && !this.pickerWindow.closed) {
            this.pickerWindow.focus();
            return;
        }
        this.addFolderErrorKey$.next(null);

        // SYNCHRONOUS open — still inside the click's call stack.
        const pickerWindow = window.open(
            'about:blank',
            SharepointFoldersContainerComponent.PICKER_WINDOW_NAME,
            SharepointFoldersContainerComponent.PICKER_WINDOW_FEATURES,
        );
        this.pickerWindow = pickerWindow;

        // Refresh the picker token/baseUrl (normally already prefetched — see
        // `prefetchPickerData`), then wait for the FIRST terminal outcome: data ready, or a
        // failed fetch. "Not there yet" is NOT an outcome — we keep the popup open for it.
        // Without a `baseUrl` the seam resolves `[]` and closes the window itself instead of
        // guessing a SharePoint host.
        this.integrationProvider.getSharepointPickerToken(connectionId);
        combineLatest([
            this.integrationProvider.getSharepointPickerToken$(),
            this.integrationProvider.getSharepointPickerBaseUrl$(),
            this.integrationProvider.getSharepointPickerTokenError$(),
        ])
            .pipe(
                map(([token, baseUrl, tokenError]) => {
                    if (tokenError != null) return { failed: true, baseUrl: null } as const;
                    if (token != null) return { failed: false, baseUrl } as const;
                    // Still in flight — emit nothing so the popup stays open and waits.
                    return null;
                }),
                filter((outcome): outcome is { failed: boolean; baseUrl: string | null } =>
                    outcome != null,
                ),
                take(1),
                // Destroyed while still waiting: `finalize` runs on unsubscribe too, so the
                // blank popup is closed rather than orphaned.
                takeUntil(this.destroy$),
                finalize(() => {
                    if (!this.pickerHandedOver) {
                        this.closePickerWindow();
                    }
                }),
            )
            .subscribe(({ failed, baseUrl }) => {
                // Terminal failure: leave `pickerHandedOver` false so `finalize` (which runs
                // straight after this handler, `take(1)` having completed) closes the popup.
                if (failed) {
                    return;
                }
                // Without the tenant's SharePoint host the picker cannot open at all — almost
                // always because the Microsoft account has no SharePoint (no licence). Say so
                // instead of closing the popup silently; `finalize` closes it.
                if (baseUrl == null) {
                    this.addFolderErrorKey$.next('aumico_integration_sharepoint_no_site');
                    return;
                }
                // From here the seam owns the window: it navigates it and closes it on
                // pick/cancel/error.
                this.pickerHandedOver = true;
                void this.pickerService
                    .openPicker(connectionId, { baseUrl, targetWindow: pickerWindow })
                    .then((picked) => {
                        this.pickerWindow = null;
                        this.pickerHandedOver = false;
                        if (picked.length > 0) {
                            // Reuse the SAME connectionId resolved above (the BE DTO requires
                            // it); re-reading `currentConnection` here could pick up a
                            // newer/emptier snapshot emitted while the picker was open.
                            this.integrationProvider.addSharepointFolders(
                                this.projectId,
                                connectionId,
                                picked,
                            );
                        }
                    })
                    .catch(() => {
                        // The seam contract is resolve-never-reject, but a throwing
                        // implementation must not orphan the popup either.
                        this.pickerHandedOver = false;
                        this.closePickerWindow();
                    });
            });
    }

    /** Close and forget the popup we opened. Safe to call repeatedly. */
    private closePickerWindow(): void {
        try {
            this.pickerWindow?.close();
        } catch {
            /* window may already be gone */
        }
        this.pickerWindow = null;
    }

    /** The remove (×) control on a folder row. */
    public onRemove(folderId: string): void {
        this.integrationProvider.removeSharepointFolder(this.projectId, folderId);
    }

    /**
     * The 'inkl. Unterordner' checkbox on a folder row (AC B3).
     *
     * Default stays TRUE — a folder is picked WITH its subfolders, and unchecking is the
     * deliberate narrowing. The reducer applies the new value optimistically, so the box
     * never lags the click.
     */
    public onToggleSubfolders({ id, includeSubfolders }: { id: string; includeSubfolders: boolean }): void {
        this.integrationProvider.updateSharepointFolder(this.projectId, id, includeSubfolders);
    }
}
